import { Box, Button, Collapse, Grid, Typography, useMediaQuery } from '@mui/material'
import { FC, useState } from 'react'

import { ExpandLess, ExpandMore } from '@mui/icons-material'

import { theme } from '../../../theme'
import {
  getColorForHostedFamilyFollowUpType,
  getReadableAnimalAdoptionStatus,
  getReadableCannotBeAdoptedStatus,
  getReadableHostedFamilyFollowUpType,
} from '../../../../domain/Animal/AnimalDisplay'
import { DisplayChipLikeTextField } from '../../../common/DisplayChipLikeTextField'
import { MainHostedFamilyFollowUp } from './HostedFamillyFollowUpsTab'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { HostedFamilyFollowUpDtoType } from '../../../../domain/Animal/HostedFamilyFollowUpDtoType'
import { EditHostedFamilyFollowUpModal } from './EditHostedFamilyFollowUpModal'
import {
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalDto,
  ShiftHostedFamilyFollowUpDto,
} from '../../../../interactors/gen/backendClient'
import { animalButtonShouldBeDisabled } from '../../../../utils/animalButtonShouldBeDisabled'
import { DeleteFollowUpModal } from './DeleteFollowUpModal'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { removeFollowUpAndRelink } from './updateAdjacentFollowUp'

interface PropsItem {
  followUp: MainHostedFamilyFollowUp
  allFollowUps: Array<MainHostedFamilyFollowUp>
  setAllFollowUps: (allFollowUps: Array<MainHostedFamilyFollowUp>) => void
  animal: AnimalDto
  mostRecent: boolean
}

export const HostedFamilyFollowUp: FC<PropsItem> = ({ followUp, allFollowUps, setAllFollowUps, animal }) => {
  const accountsStore = useAccountsStore()
  const canEditAnimal = useCanEditAnimal()
  const [isExpanded, setExpanded] = useState(false)
  const isCompact = useMediaQuery('(max-width:899px)')
  const showCompanionInline = useMediaQuery('(min-width:1200px)')

  const handleClickOnExpand = () => {
    setExpanded(!isExpanded)
  }
  const handleClickOnDelete = () => {
    try {
      const updatedFollowUps = removeFollowUpAndRelink(allFollowUps, followUp)
      setAllFollowUps(updatedFollowUps)
    } catch (error) {
      console.error('Error removing follow-up:', error)
      // You might want to show a toast notification here
      alert('Impossible de supprimer ce suivi : ' + (error as Error).message)
    }
  }

  const getAccountLabelFromId = (id: string): string => {
    if (!id) {
      return ''
    }

    const account = accountsStore.withRemovedMembers.find((account) => account.id === id)
    if (!account) {
      return `${id} (Introuvable)`
    }
    let label = account ? fullName(account) : ''

    if (account.id === accountsStore.connectedAccount?.id) {
      label = `Moi (${label})`
    }

    if (account.isDeleted) {
      label = `Compte supprimé (${label})`
    }

    return label
  }

  const renderInlineField = (label: string, value: string, maxWidth?: string) => (
    <Box
      sx={{ minWidth: 0, maxWidth, minHeight: 42, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
    >
      <Typography
        variant="caption"
        sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: '#203864',
          fontSize: { xs: 13, sm: 13.5 },
          fontWeight: 400,
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={value}
      >
        {value || '-'}
      </Typography>
    </Box>
  )

  const hasMovementFields =
    followUp.type === HostedFamilyFollowUpDtoType.InitialPlacement ||
    followUp.type === HostedFamilyFollowUpDtoType.Shift ||
    followUp.type === HostedFamilyFollowUpDtoType.Exit

  const movementDestination = hasMovementFields ? (followUp as ShiftHostedFamilyFollowUpDto).destination : undefined

  const destinationValue =
    followUp.type === HostedFamilyFollowUpDtoType.Exit
      ? Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus as Record<string, string>).includes(
          movementDestination || ''
        )
        ? getReadableCannotBeAdoptedStatus(movementDestination as AnimalAdoptionDtoCannotBeAdoptedStatus)
        : getReadableAnimalAdoptionStatus(movementDestination as AnimalAdoptionDtoStatus)
      : movementDestination
      ? movementDestination.startsWith('A-')
        ? getAccountLabelFromId(movementDestination)
        : movementDestination
      : ''

  const expandButtonSx = {
    minWidth: 0,
    width: { xs: 28, sm: 32 },
    height: { xs: 28, sm: 32 },
    p: 0,
    borderRadius: 2,
    color: '#F3AC00',
    backgroundColor: 'rgba(243, 172, 0, 0.08)',
    '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
  }

  return (
    <>
      <Box
        sx={{
          py: 1,
          borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
          '&:last-of-type': { borderBottom: 'none' },
        }}
      >
        {isCompact ? (
          <Grid container columnSpacing={0.5} rowSpacing={0} alignItems="center" sx={{ margin: 0, width: '100%' }}>
            <Grid item xs={3.1}>
              {renderInlineField('Date', followUp.date, '92px')}
            </Grid>
            <Grid item xs={4.9}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 42 }}>
                <DisplayChipLikeTextField
                  label=""
                  value={getReadableHostedFamilyFollowUpType(followUp.type)}
                  color={getColorForHostedFamilyFollowUpType(followUp.type)}
                  fullWidth
                  compact
                  borderless
                  emphasis
                  maxChipWidth={140}
                />
              </Box>
            </Grid>
            {canEditAnimal && (
              <Grid item xs={4}>
                <Box display="flex" alignItems="center" sx={{ height: '100%', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Button onClick={handleClickOnExpand} sx={expandButtonSx}>
                    {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                  </Button>
                  <EditHostedFamilyFollowUpModal
                    followUp={followUp}
                    allFollowUps={allFollowUps}
                    setAllFollowUps={setAllFollowUps}
                    animal={animal}
                    disabled={animalButtonShouldBeDisabled(animal) || !accountsStore.isPremium() || !canEditAnimal}
                    iconButtonSx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                  />
                  <DeleteFollowUpModal
                    handleClickOnDelete={handleClickOnDelete}
                    disabled={animalButtonShouldBeDisabled(animal) || !accountsStore.isPremium() || !canEditAnimal}
                    animalName={animal.name}
                    followUp={followUp}
                    allFollowUps={allFollowUps}
                    iconButtonSx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px minmax(0, 2fr) 124px',
              columnGap: 4,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 42 }}>
              <DisplayChipLikeTextField
                label=""
                value={getReadableHostedFamilyFollowUpType(followUp.type)}
                color={getColorForHostedFamilyFollowUpType(followUp.type)}
                fullWidth
                compact
                borderless
                emphasis
                maxChipWidth={140}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  followUp.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily
                    ? showCompanionInline
                      ? 'minmax(120px, 0.8fr) minmax(190px, 1fr) minmax(190px, 1fr)'
                      : 'minmax(120px, 0.8fr) minmax(260px, 1.15fr)'
                    : showCompanionInline
                    ? 'minmax(120px, 0.8fr) minmax(190px, 1fr) minmax(190px, 1fr)'
                    : 'minmax(120px, 0.8fr) minmax(260px, 1.15fr)',
                columnGap: showCompanionInline ? 3 : 2.25,
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {renderInlineField('Date', followUp.date)}
              {hasMovementFields && renderInlineField('Destination', destinationValue)}
              {hasMovementFields &&
                showCompanionInline &&
                renderInlineField('Accompagnateur', getAccountLabelFromId(followUp.memberInChargeAccountId))}
              {followUp.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily &&
                renderInlineField("Ancienne famille d'accueil", getAccountLabelFromId(followUp.beforeAccountId))}
              {followUp.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily &&
                showCompanionInline &&
                renderInlineField("Nouvelle famille d'accueil", getAccountLabelFromId(followUp.afterAccountId))}
            </Box>
            {canEditAnimal && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ width: 124, gap: 0.5 }}>
                <Button onClick={handleClickOnExpand} sx={expandButtonSx}>
                  {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                </Button>
                <EditHostedFamilyFollowUpModal
                  followUp={followUp}
                  allFollowUps={allFollowUps}
                  setAllFollowUps={setAllFollowUps}
                  animal={animal}
                  disabled={animalButtonShouldBeDisabled(animal) || !accountsStore.isPremium() || !canEditAnimal}
                  iconButtonSx={{ width: 32, height: 32 }}
                />
                <DeleteFollowUpModal
                  handleClickOnDelete={handleClickOnDelete}
                  disabled={animalButtonShouldBeDisabled(animal) || !accountsStore.isPremium() || !canEditAnimal}
                  animalName={animal.name}
                  followUp={followUp}
                  allFollowUps={allFollowUps}
                  iconButtonSx={{ width: 32, height: 32 }}
                />
              </Box>
            )}
          </Box>
        )}
        <Collapse in={isExpanded}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {(isCompact || !showCompanionInline) && hasMovementFields && (
              <>
                <Grid item sm={6} xs={12}>
                  {renderInlineField('Destination', destinationValue)}
                </Grid>
                <Grid item sm={6} xs={12}>
                  {renderInlineField('Accompagnateur', getAccountLabelFromId(followUp.memberInChargeAccountId))}
                </Grid>
              </>
            )}
            {(isCompact || !showCompanionInline) && followUp.type === HostedFamilyFollowUpDtoType.ChangeOfHostedFamily && (
              <>
                <Grid item sm={6} xs={12}>
                  {renderInlineField("Ancienne famille d'accueil", getAccountLabelFromId(followUp.beforeAccountId))}
                </Grid>
                <Grid item sm={6} xs={12}>
                  {renderInlineField("Nouvelle famille d'accueil", getAccountLabelFromId(followUp.afterAccountId))}
                </Grid>
              </>
            )}
            <Grid item sm={6} xs={12}>
              {renderInlineField('Détails', followUp.description || '-')}
            </Grid>
            <Grid item sm={6} xs={12}>
              <Box
                display="flex"
                flexDirection={{ xs: 'row', sm: 'column' }}
                justifyContent={{ xs: 'space-between', sm: 'center' }}
                alignItems="left"
                padding={1}
                paddingTop={1.2}
                gap={0.5}
                sx={{ color: theme.palette.grey[500] }}
              >
                {followUp.created && (
                  <Typography variant="body2" fontSize={14} sx={{ p: 0, m: 0 }}>
                    Créé le {new Date(followUp.created.at).toLocaleDateString()} par{' '}
                    {getAccountLabelFromId(followUp.created.by)}
                  </Typography>
                )}
                {followUp.modified && (
                  <Typography variant="body2" fontSize={14} sx={{ p: 0, m: 0 }}>
                    Modifié le {new Date(followUp.created.at).toLocaleDateString()} par{' '}
                    {getAccountLabelFromId(followUp.created.by)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </>
  )
}
